import React, { useEffect, useRef, useState } from "react";
import { Button, Icon, SpaceBetween } from "@amzn/awsui-components-react";
import { useAppDispatch, useAppSelector } from "app/store";
import { setAssistantOpen } from "features/assistant/slice";
import styles from "./style.module.scss";

const MIN_WIDTH = 400;
const MAX_WIDTH_RATIO = 0.7;
const DEFAULT_WIDTH = 540;
const STORAGE_KEY = "gaia-sidebar-width";

interface SidebarChatbotDrawerProps {
  headerActions?: React.ReactNode;
  children: React.ReactNode;
}

const SidebarChatbotDrawer: React.FC<SidebarChatbotDrawerProps> = ({
  headerActions,
  children,
}) => {
  const reduxDispatch = useAppDispatch();
  const isOpen = useAppSelector((s) => s.assistant.isOpen);

  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? Number.parseInt(stored, 10) : DEFAULT_WIDTH;
  });
  const [isResizing, setIsResizing] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);

  const widthRef = useRef(sidebarWidth);
  widthRef.current = sidebarWidth;

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const maxWidth = window.innerWidth * MAX_WIDTH_RATIO;
      const newWidth = window.innerWidth - e.clientX;
      const clampedWidth = Math.max(MIN_WIDTH, Math.min(newWidth, maxWidth));
      setSidebarWidth(clampedWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      localStorage.setItem(STORAGE_KEY, String(widthRef.current));
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [isResizing]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.marginRight = `${sidebarWidth}px`;
    } else {
      document.body.style.marginRight = "";
    }
    return () => {
      document.body.style.marginRight = "";
    };
  }, [isOpen, sidebarWidth]);

  return (
    <div
      ref={sidebarRef}
      className={`${styles.sidebar} ${isOpen ? styles.sidebarOpen : ""}`}
      style={{ "--sidebar-width": `${sidebarWidth}px` } as React.CSSProperties}
      role="complementary"
      aria-label="Ask Gaia"
    >
      {isOpen && (
        <div
          className={`${styles.resizeHandle} ${isResizing ? styles.resizing : ""}`}
          onMouseDown={(e) => {
            e.preventDefault();
            setIsResizing(true);
          }}
        >
          <Icon name="drag-indicator" size="small" />
        </div>
      )}
      <div className={styles.header}>
        <span className={styles.headerTitle}>Ask Gaia</span>
        <SpaceBetween direction="horizontal" size="xs">
          {headerActions}
          <Button
            variant="icon"
            iconName="angle-right"
            onClick={() => reduxDispatch(setAssistantOpen(false))}
            ariaLabel="Close sidebar"
          />
        </SpaceBetween>
      </div>
      <div className={styles.content}>{children}</div>
    </div>
  );
};

export default SidebarChatbotDrawer;
